import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { Link } from '@reach/router'
import moment from 'moment'

import Card from '_components/card'
import WarningIcon from '_assets/icons/warning.svg'
import CheckIcon from '_assets/icons/check.svg'
import CallIcon from '_assets/icons/call.svg'
import { assignmentLightShape } from '_utils/proptypes'
import Button, { ButtonTheme } from '_components/button'
import { usePrevious } from '_utils/hooks'
import { ASSIGNMENT_TYPE } from '_constants/assignment'
import { formatUrl } from '_utils/helpers'
import useCallCountdown from '_hooks/use-call-countdown'

import styles from './styles.css'

const EARLIER_START_MINUTES = 10

const transformDuration = (duration, type) => {
  if (type === ASSIGNMENT_TYPE.VIDEO) {
    return duration === 1 ? '1 minute' : `${duration} minutes`
  }

  return duration === 1 ? '1 day' : `${duration} days`
}

const AssignmentLight = ({
  className,
  content,
  isInstructor,
  onStartMeetingClick,
  isLeaderLed,
  isStartingCall,
  enableAssignmentVideoCall,
  cardId,
}) => {
  const joinCallRef = useRef(null)
  const [isStartCallClicked, setCallStatus] = useState(false)

  const wasStartingCall = usePrevious(isStartingCall)

  const [minuteCountdown, timeToEndMeeting] = useCallCountdown({ assignment: content })

  const isCompleted = useMemo(() => content.completed, [content])

  const isEnded = useMemo(() => content?.meeting?.ended, [content])

  const isWarning = useMemo(
    () => moment(content?.dueDate).add(1, 'days').isBefore(moment()),
    [content]
  )

  const isVideo = useMemo(() => content.type === ASSIGNMENT_TYPE.VIDEO, [content])

  const getIcon = useCallback(() => {
    if (isVideo) {
      return (
        <svg
          className={styles.icon}
          aria-label="Assignment status"
          role="img"
          viewBox={CallIcon.viewBox}
        >
          <use xlinkHref={`#${CallIcon.id}`} />
        </svg>
      )
    }

    if (isCompleted || (!isCompleted && isEnded)) {
      return (
        <svg
          className={styles.icon}
          aria-label="Assignment status"
          role="img"
          viewBox={CheckIcon.viewBox}
        >
          <use xlinkHref={`#${CheckIcon.id}`} />
        </svg>
      )
    }

    if (isWarning) {
      return (
        <svg
          className={styles.icon}
          aria-label="Assignment status"
          role="img"
          viewBox={WarningIcon.viewBox}
        >
          <use xlinkHref={`#${WarningIcon.id}`} />
        </svg>
      )
    }
    return null
  }, [isCompleted, isEnded, isVideo, isWarning])

  const duration = useMemo(
    () => transformDuration(content?.meeting?.duration, content.type),
    [content]
  )

  const description = useMemo(() => {
    if (isVideo) {
      return content?.subtitle
    }

    return content?.subtitle || 'No description'
  }, [content, isVideo])

  const hasMeeting = useMemo(() => Boolean(content?.meeting), [content])

  const meetingEnded = useMemo(() => hasMeeting && content?.meeting.ended, [content, hasMeeting])

  const meetingStarted = useMemo(
    () => hasMeeting && content?.meeting?.started,
    [content, hasMeeting]
  )

  const meetingHasRecordings = useMemo(
    () => hasMeeting && content?.meeting?.recordings?.length > 0,
    [content, hasMeeting]
  )

  const onStartCallClick = useCallback(() => {
    setCallStatus(true)
    onStartMeetingClick({
      meetingId: content?.meeting.id,
      assignmentId: content.id,
    })
  }, [content, onStartMeetingClick])

  const renderButtonOrRecordings = useCallback(() => {
    if (meetingEnded) {
      return meetingHasRecordings ? null : (
        <p className={styles.disabled}> Video recording coming soon </p>
      )
    }

    const today = moment()
    const dueDate = moment(content?.meeting.startTime).add(content?.meeting.duration, 'minutes')
    const earlierStartTime = moment(content.meeting.startTime).subtract(
      EARLIER_START_MINUTES,
      'minutes'
    )
    // if is a leader-led class
    // only the instructor can start the call
    // members have access only if the call has started
    if (isLeaderLed && hasMeeting && !meetingStarted) {
      let isButtonDisabled = true

      if (isInstructor) {
        isButtonDisabled =
          today.isBefore(earlierStartTime) ||
          (today.isAfter(dueDate) &&
            (minuteCountdown > EARLIER_START_MINUTES || timeToEndMeeting <= 0))
      } else {
        isButtonDisabled = !meetingStarted || meetingEnded
      }

      return (
        <Button
          className={styles.action}
          label={isInstructor && timeToEndMeeting >= 0 ? 'START CALL' : 'JOIN CALL'}
          theme={ButtonTheme.PRIMARY}
          disabled={isButtonDisabled}
          onClick={onStartCallClick}
        />
      )
    }

    // it's a community-led class
    if (hasMeeting && (today.isBefore(earlierStartTime) || today.isAfter(dueDate))) {
      return (
        <Button className={styles.action} label="JOIN CALL" theme={ButtonTheme.PRIMARY} disabled />
      )
    }

    const callLink = formatUrl(content.meeting?.joinUrl)
    return (
      <a
        className={styles.action}
        href={callLink}
        target="_blank"
        rel="noopener noreferrer"
        ref={joinCallRef}
      >
        JOIN CALL
      </a>
    )
    // We don't want to get the nested deps, only the parent (content)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    content?.duration,
    content?.meeting?.joinUrl,
    content?.meeting?.startTime,
    hasMeeting,
    isInstructor,
    isLeaderLed,
    meetingEnded,
    meetingHasRecordings,
    meetingStarted,
    minuteCountdown,
    onStartCallClick,
    timeToEndMeeting,
  ])

  const assignmentName = useMemo(() => {
    if (content.type === ASSIGNMENT_TYPE.VIDEO) {
      return content.name || 'Live call'
    }
    return content.name
  }, [content])

  const assignmentDetails = useMemo(() => {
    const formatTime = `MMMM Do YYYY${isVideo ? ', LT' : ''}`

    if (!duration) {
      return null
    }

    if (isVideo && content?.meeting?.startTime) {
      return (
        <p className={styles.duration}>
          Starts in {moment(content?.meeting?.startTime).format(formatTime)}{' '}
          {isVideo && `• For ${duration}`} - Your local date and time
        </p>
      )
    }

    if (content?.dueDate) {
      return (
        content?.dueDate && (
          <p className={styles.duration}>
            Due {moment(content.dueDate).format(formatTime)} {isVideo && `• For ${duration}`} - Your
            local date and time
          </p>
        )
      )
    }

    return null
  }, [content, duration, isVideo])

  const renderCardContent = useCallback(
    () => (
      <>
        <div
          className={classnames(styles.status, {
            [styles.video]: isVideo,
            [styles.completed]: (isCompleted || (!isCompleted && isEnded)) && !isVideo,
            [styles.warning]: isWarning && !isEnded && !isCompleted && !isVideo,
          })}
        >
          {(isCompleted || (!isCompleted && isEnded) || isWarning || isVideo) && getIcon()}
        </div>
        <div className={classnames(styles.details)}>
          <h4 className={styles.title}>{assignmentName}</h4>
          {assignmentDetails}
          {isVideo && hasMeeting && renderButtonOrRecordings()}
          <p className={styles.description}>{description}</p>
          <div className={styles.recordings}>
            {hasMeeting
              ? meetingHasRecordings &&
                content?.meeting?.recordings.map(recording => (
                  <a
                    key={recording.recordingId}
                    href={recording.file}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.recording}
                    aria-label={`recording-${recording.filename}`}
                  >
                    {recording.filename}
                  </a>
                ))
              : null}
          </div>
        </div>
      </>
    ),
    [
      assignmentDetails,
      assignmentName,
      content,
      description,
      getIcon,
      hasMeeting,
      isCompleted,
      isEnded,
      isVideo,
      isWarning,
      meetingHasRecordings,
      renderButtonOrRecordings,
    ]
  )

  useEffect(() => {
    if (wasStartingCall && !isStartingCall && isStartCallClicked && joinCallRef.current) {
      setCallStatus(false)
      joinCallRef.current.click()
    }
  }, [isStartCallClicked, isStartingCall, wasStartingCall])

  const shouldLeaderLedStart = useMemo(
    () => isLeaderLed && hasMeeting && !content?.meeting.started,
    [
      // We don't want to get the nested deps, only the parent (content)
      // eslint-disable-next-line react-hooks/exhaustive-deps
      content,
      hasMeeting,
      isLeaderLed,
    ]
  )
  const shouldCommunityLedStart = useMemo(
    () => !isLeaderLed && hasMeeting && content?.meeting.started && !content?.meeting.ended,
    [
      // We don't want to get the nested deps, only the parent (content)
      // eslint-disable-next-line react-hooks/exhaustive-deps
      content,
      hasMeeting,
      isLeaderLed,
    ]
  )

  useEffect(() => {
    // isVideo = only for leader-led and community-led classes
    let checkVideoAssignment = null

    if (
      isVideo &&
      !content?.meeting?.canStart &&
      (shouldLeaderLedStart || shouldCommunityLedStart)
    ) {
      const dueDate = moment(content?.meeting.startTime).add(content?.meeting.duration, 'minutes')
      const earlierStartTime = moment(content?.meeting.startTime).subtract(10, 'minutes')

      checkVideoAssignment = setInterval(() => {
        const today = moment()

        if (today.isAfter(dueDate)) {
          clearInterval(checkVideoAssignment)
          return
        }

        if (today.isSameOrAfter(earlierStartTime) && today.isSameOrBefore(dueDate)) {
          enableAssignmentVideoCall(content.id)
          clearInterval(checkVideoAssignment)
        }
      }, 60000)
    }

    return () => {
      clearInterval(checkVideoAssignment)
    }
  }, [
    content,
    enableAssignmentVideoCall,
    hasMeeting,
    isInstructor,
    isLeaderLed,
    isVideo,
    shouldCommunityLedStart,
    shouldLeaderLedStart,
  ])

  return (
    <Card className={classnames(styles.container, className)} id={cardId}>
      {renderCardContent()}
      <Link to={`assignment/${content.id}`} className={styles.link} />
    </Card>
  )
}

AssignmentLight.propTypes = {
  className: PropTypes.string,
  content: assignmentLightShape.isRequired,
  isInstructor: PropTypes.bool,
  onStartMeetingClick: PropTypes.func,
  isLeaderLed: PropTypes.bool,
  isStartingCall: PropTypes.bool,
  enableAssignmentVideoCall: PropTypes.func,
  cardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

AssignmentLight.defaultProps = {
  className: '',
  isInstructor: false,
  onStartMeetingClick: () => {},
  isLeaderLed: false,
  isStartingCall: false,
  enableAssignmentVideoCall: () => {},
  cardId: undefined,
}

export default React.memo(AssignmentLight)
